import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import studentService from '../../services/studentService';
import { useStudentProgress } from '../../hooks/useStudentProgress';
import './StudentAttendancePage.css';

const StudentAttendancePage = () => {
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState([]);
  const [attendancePercentage, setAttendancePercentage] = useState('N/A');
  const [error, setError] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();

  // Get studentId from either params or location state
  const studentId = id || (location.state && location.state.studentId);
  const studentName = location.state && location.state.studentName;

  // Use the hook to get the student progress data including attendance
  const { 
    loading: hookLoading, 
    error: hookError, 
    dashboardStats,
    attendanceData,
    refreshData
  } = useStudentProgress(studentId);

  // Process attendance data from the hook
  useEffect(() => {
    if (hookLoading) {
      setLoading(true);
      return;
    }
    
    if (hookError) {
      setError(hookError);
      setLoading(false);
      return;
    }

    // Check if attendanceData exists and is an array
    if (attendanceData && Array.isArray(attendanceData)) {
      // Process and format the attendance data
      const formattedAttendance = attendanceData.map(record => {
        // Format date
        const date = new Date(record.date);
        const formattedDate = date.toLocaleDateString('en-US', { 
          weekday: 'long',
          year: 'numeric', 
          month: 'long', 
          day: 'numeric'
        });
          
        // Determine colors based on status
        let statusColor, backgroundColor, statusText;
          
        switch(record.status) {
          case 'present':
            statusColor = '#2e7d32';
            backgroundColor = '#e8f5e9';
            statusText = 'Present';
            break;
          case 'absent':
            statusColor = '#d32f2f';
            backgroundColor = '#ffebee';
            statusText = 'Absent';
            break;
          case 'late':
            statusColor = '#ff8f00';
            backgroundColor = '#fff8e1';
            statusText = 'Late';
            break;
          default:
            statusColor = '#757575';
            backgroundColor = '#f5f5f5';
            statusText = 'Unknown';
        }
          
        return {
          id: record._id,
          date: formattedDate,
          rawDate: date,
          status: record.status,
          statusText,
          statusColor,
          backgroundColor,
          halaqa: record.halaqaId ? (record.halaqaId.name || 'Unknown Class') : 'Unknown Class',
          markedBy: record.markedBy ? (record.markedBy.name || 'Unknown') : 'Unknown',
          comments: record.comments || '',
        };
      });
        
      // Sort by date (most recent first)
      formattedAttendance.sort((a, b) => b.rawDate - a.rawDate);
      
      setAttendance(formattedAttendance);
      
      // Use attendance rate from dashboard stats
      if (dashboardStats && dashboardStats.attendanceRate) {
        setAttendancePercentage(`${dashboardStats.attendanceRate}%`);
      } else if (formattedAttendance.length > 0) {
        // Calculate as fallback if not provided by the hook
        const presentCount = formattedAttendance.filter(a => a.status === 'present').length;
        const totalCount = formattedAttendance.length;
        const percentage = Math.round((presentCount / totalCount) * 100);
        setAttendancePercentage(`${percentage}%`);
      } else {
        setAttendancePercentage('N/A');
      }
    } else {
      setAttendance([]);
      setAttendancePercentage('N/A');
    }
    
    setLoading(false);
  }, [hookLoading, hookError, attendanceData, dashboardStats]);

  const isPast = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(date) < today;
  };

  if (loading || hookLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading attendance history...</p>
      </div>
    );
  }

  return (
    <div className="student-attendance-page">
      <div className="page-header">
        <h1>{studentName ? `${studentName}'s Attendance History` : 'Attendance History'}</h1>
        {location.state?.fromParentChildView ? (
          <Link to={`/parent/children/${studentId}`} className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
            Back to Child Profile
          </Link>
        ) : (
          <Link to={`/students/${studentId}`} className="back-link">
            <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
            Back to Student Profile
          </Link>
        )}
      </div>

      <div className="summary-container">
        <h2 className="summary-title">Attendance Summary</h2>
        
        <div className="percentage-container">
          <div className="percentage-value">{attendancePercentage}</div>
          <div className="percentage-label">Attendance Rate</div>
        </div>
        
        <div className="stats-row">
          <div className="stat-item">
            <div className="stat-value">
              {attendance.filter(a => a.status === 'present').length}
            </div>
            <div className="stat-label">Present</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">
              {attendance.filter(a => a.status === 'absent').length}
            </div>
            <div className="stat-label">Absent</div>
          </div>
          
          <div className="stat-item">
            <div className="stat-value">
              {attendance.filter(a => a.status === 'late').length}
            </div>
            <div className="stat-label">Late</div>
          </div>
        </div>
      </div>

      <h2 className="section-title">Attendance History</h2>
      
      {error ? (
        <div className="error-container">
          <p className="error-text">{error}</p>
          <button 
            className="retry-button"
            onClick={() => {
              setLoading(true);
              // Use the refreshData function if provided by the hook
              if (typeof refreshData === 'function') {
                refreshData();
              } else {
                // Re-run the effect by changing a dependency
                navigate({ state: { studentId, studentName, refresh: new Date().getTime() } });
              }
            }}
          >
            Retry
          </button>
        </div>
      ) : attendance.length === 0 ? (
        <div className="empty-container">
          <p className="empty-text">No attendance records found</p>
        </div>
      ) : (
        <div className="attendance-list">
          {attendance.map((item) => (
            <div key={item.id} className="attendance-card">
              <div 
                className="status-indicator" 
                style={{ backgroundColor: item.statusColor }}
              ></div>
              
              <div className="attendance-content">
                <h3 className="date-text">{item.date}</h3>
                <div className="halaqa-text">{item.halaqa}</div>
                
                <div 
                  className="status-badge"
                  style={{ backgroundColor: item.backgroundColor }}
                >
                  <span style={{ color: item.statusColor }} className="status-text">
                    {item.statusText}
                  </span>
                </div>
                
                {item.comments && (
                  <div className="comments-text">
                    <span className="comments-label">Comments: </span>
                    {item.comments}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentAttendancePage;